// Shared FAQ content. Imported by page components (visible accordions) AND by
// routesMeta.js (FAQPage JSON-LD) so the two never drift. Plain data only.

export const faqs = {
  pricing: [
    {
      q: 'How much does Limosys cost?',
      a: 'Limosys is priced per operation based on your fleet size, the modules you use (dispatch, driver and passenger apps, website integration, accounting), and your trip volume. Because every ground transportation business is different, we quote you directly rather than forcing you into a rigid plan. Request a demo and we will build a package that fits your operation and budget.',
    },
    {
      q: 'Do you charge per-trip commissions?',
      a: 'No. Limosys uses transparent licensing rather than taking a commission on every reservation, so your costs stay predictable as you grow. You keep the revenue you earn.',
    },
    {
      q: 'Is there a setup or onboarding fee?',
      a: 'Onboarding includes personalized setup, data migration, and training. We scope this with you up front so there are no surprises, and our team stays with you after go-live with 24/7 support.',
    },
    {
      q: 'Can I add or remove features later?',
      a: 'Yes. The platform is modular and fully customizable. You can start with dispatch and back-office tools and add branded driver/passenger apps, website reservations, or custom add-ons whenever you are ready.',
    },
    {
      q: 'Is there a contract or can I cancel?',
      a: 'We offer flexible licensing designed for operators of any size. Talk to us about month-to-month and annual options during your demo.',
    },
  ],

  dispatch: [
    {
      q: 'What is limo dispatch software?',
      a: 'Limo dispatch software is the system operators use to book reservations, assign trips to the right vehicle and chauffeur, track the fleet in real time, and communicate with drivers and passengers. Limosys combines manual and fully automated auto-dispatch in one platform.',
    },
    {
      q: 'How does auto-dispatch decide which vehicle to assign?',
      a: 'Auto-dispatch matches each trip to the best available vehicle by vehicle type, passenger capacity, pick-up time and location, and any special requirements, then routes the job to the assigned chauffeur automatically — reducing manual work and dispatcher errors.',
    },
    {
      q: 'Can I still dispatch manually when I need to?',
      a: 'Yes. Dispatchers keep full manual control and can override auto-assignments at any time. Most operators run a hybrid: automation for routine trips and manual control for VIPs and exceptions.',
    },
    {
      q: 'Does it reduce no-shows and last-minute cancellations?',
      a: 'Automated voice and text confirmations remind passengers and confirm details ahead of the pick-up, which reduces no-shows and the payroll cost of unnecessary dispatches.',
    },
  ],

  reservation: [
    {
      q: 'Can customers book directly from my website?',
      a: 'Yes. Limosys adds a full reservation system to your own website so customers can get instant quotes, enter flight details, schedule rides, and pay — with every booking flowing straight into your dispatch system.',
    },
    {
      q: 'Do reservations sync automatically with dispatch?',
      a: 'Every reservation — from your website, branded passenger app, or entered by staff — lands in the same dashboard in real time, so there is no double entry and nothing falls through the cracks.',
    },
    {
      q: 'Can passengers manage and repeat past trips?',
      a: 'Yes. Customer profiles store trip history and payment methods so repeat bookings are fast and rides run smoothly for returning clients.',
    },
  ],

  driverApp: [
    {
      q: 'What can chauffeurs do in the Limosys driver app?',
      a: 'Drivers receive trip details instantly, navigate with real-time GPS, update trip status, communicate with dispatch and passengers, capture electronic signatures, and process payments in the vehicle.',
    },
    {
      q: 'Does dispatch see driver location in real time?',
      a: 'Yes. Dispatchers track chauffeur progress live on GPS, so they can give passengers accurate ETAs and manage the day as it unfolds.',
    },
    {
      q: 'Is the driver app available on iOS and Android?',
      a: 'Yes, the driver app runs on both iOS and Android devices.',
    },
  ],

  passengerApp: [
    {
      q: 'Is the passenger app branded for my company?',
      a: 'Yes. The passenger app is white-labeled with your brand, so your customers book, track, and manage trips under your name — not a third-party marketplace.',
    },
    {
      q: 'What can passengers do in the app?',
      a: 'Passengers can book and schedule rides in advance, preview rates before booking, watch their vehicle approach in real time, message the driver, store payment methods, and review past trips.',
    },
    {
      q: 'Does it help me compete with rideshare apps?',
      a: 'A branded booking experience gives your loyal customers the on-demand convenience they expect from rideshare while keeping your premium service and relationship front and center.',
    },
  ],

  accounting: [
    {
      q: 'What accounting and billing does Limosys handle?',
      a: 'Limosys manages the full billing cycle: quotes and invoices, integrated credit card processing, payroll disbursements, and expense tracking — all tied to the trips your operation actually ran.',
    },
    {
      q: 'Can I process credit cards inside the platform?',
      a: 'Yes. Payment processing is built in, including in-vehicle payments through the driver app and stored payment methods for repeat customers.',
    },
    {
      q: 'What reports can I generate?',
      a: 'You can produce comprehensive operational and financial reports for complete insight into revenue, costs, fleet utilization, and performance.',
    },
  ],

  limoAnywhere: [
    {
      q: 'Is Limosys a good alternative to Limo Anywhere?',
      a: 'Yes. Limosys offers the same core capabilities operators expect — reservations, dispatch and auto-dispatch, branded driver and passenger apps, billing, and reporting — backed by 30+ years serving ground transportation companies, a 25,000-car affiliate network, transparent licensing, and 24/7 support with a real team behind it.',
    },
    {
      q: 'Will you help me migrate my data?',
      a: 'Yes. Onboarding includes data migration and personalized training so the switch is smooth and you are operational quickly.',
    },
    {
      q: 'How is pricing different?',
      a: 'Limosys uses transparent licensing tailored to your operation instead of per-trip commissions, which keeps costs predictable as your volume grows.',
    },
    {
      q: 'Can I keep my branded apps and website booking?',
      a: 'Yes. You get white-labeled driver and passenger apps and a reservation system that embeds directly into your own website.',
    },
  ],

  moovs: [
    {
      q: 'How does Limosys compare to Moovs?',
      a: 'Both cover booking and dispatch, but Limosys is built for operators who want deep, customizable dispatch and back-office tools, a large affiliate network, and a support team with three decades of ground transportation experience — from small fleets to complex multi-vehicle operations.',
    },
    {
      q: 'Is Limosys suitable for growing fleets?',
      a: 'Yes. The platform scales from a handful of vehicles to large operations, with flexible licensing and add-ons you can enable as you grow.',
    },
  ],

  groundTransportation: [
    {
      q: 'What is ground transportation software?',
      a: 'Ground transportation software runs the operational side of a chauffeured, livery, black car, shuttle, or limousine business: reservations, dispatch, fleet and driver management, passenger communication, payments, and reporting — in one connected system.',
    },
    {
      q: 'What kinds of operators use Limosys?',
      a: 'Black car and livery services, limousine companies, airport and corporate transportation providers, shuttle operators, and non-emergency medical and funeral transportation businesses all run on Limosys.',
    },
    {
      q: 'Does Limosys work for both small and large fleets?',
      a: 'Yes. Limosys is fully customizable with flexible licensing, so it fits owner-operators as well as large multi-city fleets.',
    },
  ],

  blackCar: [
    { q: 'Is Limosys good for black car and livery operators?', a: 'Yes. Limosys was built for premium black car and livery work: branded booking, reliable auto-dispatch with manual override for VIPs, real-time GPS, corporate accounts, and professional invoicing.' },
    { q: 'Does it support corporate accounts and repeat clients?', a: 'Yes. Customer and corporate profiles store preferences, trip history, and payment methods so repeat bookings are fast and rides run smoothly.' },
  ],
  airport: [
    { q: 'Does Limosys handle airport trips and flight tracking?', a: 'Yes. Reservations capture flight information, and the platform supports airport pick-ups and meet-and-greet style workflows with real-time updates.' },
    { q: 'Can passengers enter flight details when booking?', a: 'Yes. Customers can enter flight information directly during online booking so your team is ready for delays and schedule changes.' },
  ],
  corporate: [
    { q: 'Is Limosys suitable for corporate transportation?', a: 'Yes. Corporate accounts, profiles, branded booking, reliable dispatch, and clean invoicing make Limosys a strong fit for corporate and executive ground transportation.' },
    { q: 'Can we manage multiple corporate accounts?', a: 'Yes. You can create and manage corporate profiles with their own booking and billing details.' },
  ],
  shuttle: [
    { q: 'Can Limosys handle shuttle and shared-ride services?', a: 'Yes. Limosys supports scheduled and shared-ride operations with dispatch, routing, and passenger communication tools.' },
    { q: 'Does it support recurring and scheduled routes?', a: 'Yes. Customers and staff can schedule rides in advance, including recurring trips.' },
  ],
  nemt: [
    { q: 'Does Limosys work for non-emergency medical transportation (NEMT)?', a: 'Yes. NEMT providers use Limosys for scheduling, dispatch, vehicle and capacity matching, and trip documentation, with automated reminders to reduce no-shows.' },
    { q: 'Can it match vehicles to special requirements?', a: 'Yes. Auto-dispatch matches trips to vehicles by type, capacity, and special needs.' },
  ],
  funeral: [
    { q: 'Is Limosys used for funeral and livery transportation?', a: 'Yes. Funeral and livery operators use Limosys to coordinate vehicles, chauffeurs, and schedules with the reliability and discretion these services require.' },
    { q: 'Can we customize it to our specific services?', a: 'Yes. The platform is fully customizable with add-ons available on request.' },
  ],
  local: [
    { q: 'Does Limosys work for operators in this area?', a: 'Yes. Limosys serves ground transportation operators across the United States, including the New York and New Jersey metro area, and is headquartered in Englewood Cliffs, NJ.' },
    { q: 'Can you help us migrate and get set up quickly?', a: 'Yes. Onboarding includes data migration, personalized training, and 24/7 support so you are operational quickly.' },
  ],

  general: [
    {
      q: 'How long has Limosys been in business?',
      a: 'Limosys has powered ground transportation operations for over 30 years, since 1990.',
    },
    {
      q: 'What support do you offer?',
      a: 'We provide 24/7 support and service, personalized onboarding, and a dedicated team that knows the ground transportation industry.',
    },
    {
      q: 'Can Limosys be customized to my operation?',
      a: 'Yes. The platform is fully customizable with add-ons available on request and flexible licensing for any fleet size.',
    },
  ],
};

// Curated set for the dedicated /faq page (drawn from the topic sets above).
export const siteFaq = [
  faqs.groundTransportation[0],
  faqs.dispatch[0],
  faqs.dispatch[1],
  faqs.reservation[0],
  faqs.passengerApp[0],
  faqs.driverApp[0],
  faqs.accounting[0],
  faqs.pricing[0],
  faqs.pricing[1],
  faqs.general[0],
  faqs.general[1],
  faqs.general[2],
];
